// Data access for the admin document generator. Every table here is admin-only
// by RLS (migration 20260928083451); this file adds no authority of its own.
//
// ⚠️ SCOPE: this tool writes ONLY to doc_* tables. It reads `requests` and
// `profiles` to find and prefill a request, and never writes to either, nor to
// offers or any platform price (owner, 2026-09-28). There is deliberately no
// sb.from('requests').insert/update/upsert anywhere in src/lib/docgen or the
// docgen pages — the merge check greps for it.

import { sb } from '../../supabase';
import { defaultQuotationContent, defaultRfqContent } from './content.js';

import { newId } from './ids.js';
import { copyImagesTo, removeDocumentFolder } from './images.js';

export { newId };

const fail = (where, error) => {
  console.error(`[docgen] ${where}:`, error?.message || error);
  throw error;
};

// ── lists and search ────────────────────────────────────────────────────
export async function listDocuments({ limit = 200 } = {}) {
  const { data, error } = await sb.from('doc_documents')
    .select('id, kind, layout, status, ref_no, revision, parent_id, currency, content, created_at, updated_at, approved_at, request_id, client_id, requests(request_ref), doc_clients(name, company)')
    .order('updated_at', { ascending: false })
    .limit(limit);
  if (error) fail('listDocuments', error);
  return data || [];
}

// PostgREST .or() is a comma/paren-delimited grammar: strip its delimiters and
// quote the value, so a search like "12,5 (A)" cannot change the filter.
const escLike = (s) => s.replace(/[%_,()"\\]/g, ' ').trim();
const ilike = (col, s) => `${col}.ilike."%${s}%"`;

// Existing platform requests, by reference, title or the buyer's name. Read-only.
export async function searchRequests(q) {
  const s = escLike(String(q || ''));
  const cols = 'id, request_ref, title_ar, title_en, request_kind, sourcing_mode, status, created_at, buyer_id, profiles:buyer_id(full_name, company_name)';
  const base = sb.from('requests').select(cols).order('created_at', { ascending: false }).limit(20);
  if (!s) {
    const { data, error } = await base;
    if (error) fail('searchRequests', error);
    return data || [];
  }
  const [byText, byBuyer] = await Promise.all([
    sb.from('requests').select(cols)
      .or([ilike('request_ref', s), ilike('title_ar', s), ilike('title_en', s)].join(','))
      .order('created_at', { ascending: false }).limit(20),
    sb.from('profiles').select('id').or([ilike('full_name', s), ilike('company_name', s)].join(',')).limit(20),
  ]);
  if (byText.error) fail('searchRequests', byText.error);
  if (byBuyer.error) fail('searchRequests(buyer)', byBuyer.error);
  let rows = byText.data || [];
  const ids = (byBuyer.data || []).map((p) => p.id);
  if (ids.length) {
    const { data, error } = await sb.from('requests').select(cols).in('buyer_id', ids)
      .order('created_at', { ascending: false }).limit(20);
    if (error) fail('searchRequests(byBuyer)', error);
    const seen = new Set(rows.map((r) => r.id));
    rows = rows.concat((data || []).filter((r) => !seen.has(r.id)));
  }
  return rows.slice(0, 30);
}

export async function getRequest(id) {
  const { data, error } = await sb.from('requests')
    .select('id, request_ref, title_ar, title_en, request_kind, sourcing_mode, status, created_at, buyer_id, profiles:buyer_id(full_name, company_name)')
    .eq('id', id).maybeSingle();
  if (error) fail('getRequest', error);
  return data;
}

export async function searchClients(q) {
  const s = escLike(String(q || ''));
  let query = sb.from('doc_clients').select('id, name, company, phone, phone_e164, notes, created_at')
    .order('updated_at', { ascending: false }).limit(30);
  if (s) query = query.or([ilike('name', s), ilike('company', s), ilike('phone', s), ilike('phone_e164', s)].join(','));
  const { data, error } = await query;
  if (error) fail('searchClients', error);
  return data || [];
}

export async function findClientByPhone(e164) {
  if (!e164) return null;
  const { data, error } = await sb.from('doc_clients').select('*').eq('phone_e164', e164).maybeSingle();
  if (error) fail('findClientByPhone', error);
  return data;
}

export async function createClient({ name, company, phone, phone_e164, notes }) {
  const { data, error } = await sb.from('doc_clients')
    .insert({ name: name.trim(), company: company?.trim() || null, phone: phone || null, phone_e164: phone_e164 || null, notes: notes?.trim() || null })
    .select('*').single();
  if (error) fail('createClient', error);
  return data;
}

// ── create ──────────────────────────────────────────────────────────────
function clientNameFrom({ request, client }) {
  if (client) return client.company ? `${client.name} — ${client.company}` : client.name;
  const p = request?.profiles;
  return p?.company_name || p?.full_name || '';
}

/**
 * A new draft with one empty section. `request` / `client` are the rows
 * picked on the "Who is this for?" step (or neither, standalone).
 */
export async function createDocument({ kind, layout, request = null, client = null }) {
  const isRfq = kind === 'rfq';
  const content = isRfq ? defaultRfqContent() : defaultQuotationContent();
  if (!isRfq) {
    content.clientName = clientNameFrom({ request, client });
    content.subject = request ? (request.title_ar || request.title_en || '') : '';
  }
  const doc = {
    id: newId(),
    kind,
    layout: isRfq ? 'standard' : (layout === 'itemized' ? 'itemized' : 'simple'),
    request_id: request?.id || null,
    client_id: client?.id || null,
    currency: 'SAR',
    content,
  };
  const { error } = await sb.from('doc_documents').insert(doc);
  if (error) fail('createDocument', error);
  const { error: sErr } = await sb.from('doc_sections').insert({ id: newId(), document_id: doc.id, position: 0, title: '' });
  if (sErr) fail('createDocument(section)', sErr);
  return doc.id;
}

// ── load / save ─────────────────────────────────────────────────────────
export async function loadDocument(id) {
  const [d, s, l, i] = await Promise.all([
    sb.from('doc_documents').select('*, requests(id, request_ref, title_ar, title_en, request_kind, status), doc_clients(id, name, company, phone)').eq('id', id).single(),
    sb.from('doc_sections').select('*').eq('document_id', id).order('position'),
    sb.from('doc_lines').select('*').eq('document_id', id).order('position'),
    sb.from('doc_images').select('*').eq('document_id', id).order('position'),
  ]);
  for (const [name, r] of [['document', d], ['sections', s], ['lines', l], ['images', i]]) {
    if (r.error) fail(`loadDocument(${name})`, r.error);
  }
  return { doc: d.data, sections: s.data || [], lines: l.data || [], images: i.data || [] };
}

const numOrNull = (v) => (v === '' || v === null || v === undefined || Number.isNaN(Number(v)) ? null : Number(v));
const DOC_FIELDS = ['layout', 'currency', 'incoterm', 'port', 'agent_pct', 'margin_pct', 'fx_usd_sar', 'fx_cny_usd',
  'deposit_pct', 'paid_amount', 'valid_until', 'content'];
const NUM_DOC = new Set(['agent_pct', 'margin_pct', 'fx_usd_sar', 'fx_cny_usd', 'deposit_pct', 'paid_amount']);
const NOT_NULL_DEFAULTS = { agent_pct: 0, margin_pct: 10, fx_usd_sar: 3.78, deposit_pct: 50, paid_amount: 0 };

function docPatch(doc) {
  const out = {};
  for (const k of DOC_FIELDS) {
    let v = doc[k];
    if (NUM_DOC.has(k)) v = numOrNull(v) ?? (k in NOT_NULL_DEFAULTS ? NOT_NULL_DEFAULTS[k] : null);
    if (k === 'valid_until' || k === 'incoterm' || k === 'port') v = v || null;
    out[k] = v;
  }
  return out;
}

const sectionRow = (s, docId, i) => ({
  id: s.id, document_id: docId, position: i, title: s.title || '',
  units: Math.max(1, parseInt(s.units, 10) || 1),
  lump_cost: numOrNull(s.lump_cost),
  lump_currency: numOrNull(s.lump_cost) === null ? null : (s.lump_currency || 'USD'),
});
const lineRow = (l, docId, i) => ({
  id: l.id, document_id: docId, section_id: l.section_id, position: i,
  item: l.item || '', description: l.description || null, brand: l.brand || null, unit: l.unit || null,
  qty: numOrNull(l.qty), supplier_unit_cost: numOrNull(l.supplier_unit_cost),
  cost_currency: l.cost_currency || null, line_kind: l.line_kind === 'passthrough' ? 'passthrough' : 'goods',
  lead_time_text: l.lead_time_text || null, remarks: l.remarks || null,
  origin_line_id: l.origin_line_id || null,
});

/**
 * Persist a draft. Upserts by id (ids are minted client-side, so a new row and
 * an edited row take the same path) and deletes rows the editor removed.
 * Order: document, sections, lines, then deletes — a line never points at a
 * section that is not saved yet.
 */
export async function saveDraft({ doc, sections, lines, deletedSectionIds = [], deletedLineIds = [] }) {
  const { error: dErr } = await sb.from('doc_documents').update(docPatch(doc)).eq('id', doc.id);
  if (dErr) fail('saveDraft(document)', dErr);
  if (sections.length) {
    const { error } = await sb.from('doc_sections').upsert(sections.map((s, i) => sectionRow(s, doc.id, i)));
    if (error) fail('saveDraft(sections)', error);
  }
  // Lines are positioned within their section.
  const rows = [];
  for (const s of sections) lines.filter((l) => l.section_id === s.id).forEach((l, i) => rows.push(lineRow(l, doc.id, i)));
  if (rows.length) {
    const { error } = await sb.from('doc_lines').upsert(rows);
    if (error) fail('saveDraft(lines)', error);
  }
  if (deletedLineIds.length) {
    const { error } = await sb.from('doc_lines').delete().in('id', deletedLineIds);
    if (error) fail('saveDraft(delete lines)', error);
  }
  if (deletedSectionIds.length) {
    const { error } = await sb.from('doc_sections').delete().in('id', deletedSectionIds);
    if (error) fail('saveDraft(delete sections)', error);
  }
}

export async function deleteDraft(id) {
  const { data, error } = await sb.from('doc_documents').delete().eq('id', id).eq('status', 'draft').select('id');
  if (error) fail('deleteDraft', error);
  if (data?.length) await removeDocumentFolder(id); // rows cascaded; now the files
}

/**
 * Copy a document into a new draft: as a fresh document (duplicate), as the
 * next revision of an approved one (revise), or as a quotation built from an
 * RFQ (items kept, costs empty — they come from the supplier).
 */
export async function copyDocument(sourceId, mode) {
  const { doc, sections, lines, images } = await loadDocument(sourceId);
  const toQuote = mode === 'quote_from_rfq';
  const next = {
    id: newId(),
    kind: toQuote ? 'quotation' : doc.kind,
    layout: toQuote ? 'simple' : doc.layout,
    request_id: doc.request_id,
    client_id: doc.client_id,
    parent_id: mode === 'revise' ? (doc.status === 'draft' ? doc.parent_id : doc.id) : null,
    origin_document_id: toQuote ? doc.id : null,
    currency: doc.currency,
    incoterm: doc.incoterm, port: doc.port,
    agent_pct: doc.agent_pct, margin_pct: doc.margin_pct, fx_usd_sar: doc.fx_usd_sar, fx_cny_usd: doc.fx_cny_usd,
    deposit_pct: doc.deposit_pct, paid_amount: doc.paid_amount, valid_until: doc.valid_until,
    content: toQuote ? defaultQuotationContent() : doc.content,
  };
  if (mode === 'revise' && !next.parent_id) throw new Error('A revision needs an approved document');
  const { error } = await sb.from('doc_documents').insert(next);
  if (error) fail('copyDocument', error);
  const secMap = new Map(sections.map((s) => [s.id, newId()]));
  if (sections.length) {
    const { error: e } = await sb.from('doc_sections').insert(sections.map((s, i) => ({
      ...sectionRow(s, next.id, i), id: secMap.get(s.id),
      ...(toQuote ? { lump_cost: null, lump_currency: null, units: 1 } : {}),
    })));
    if (e) fail('copyDocument(sections)', e);
  }
  const lineMap = new Map(lines.map((l) => [l.id, newId()]));
  if (lines.length) {
    const { error: e } = await sb.from('doc_lines').insert(lines.map((l, i) => ({
      ...lineRow(l, next.id, l.position ?? i), id: lineMap.get(l.id), section_id: secMap.get(l.section_id),
      origin_line_id: toQuote ? l.id : l.origin_line_id || null,
      ...(toQuote ? { supplier_unit_cost: null, cost_currency: null, line_kind: 'goods' } : {}),
    })));
    if (e) fail('copyDocument(lines)', e);
  }
  // Files are copied, not shared: each document owns its images.
  if (images.length) await copyImagesTo(images, next.id, lineMap);
  return next.id;
}
